import { PAID_COLUMN_HEADINGS, REGISTRATION_COLUMN_HEADINGS, RESPONSES_INDEX } from './constants';
import { FC, ReactNode, useMemo } from 'react';

import { PunchupLoadMore } from 'features/common/components/Punchup/composites/tables/PunchupLoadMore';
import { PunchupTableBody } from './PunchupTableBody';
import { ResponsesData, ResponsesProps, SortField } from 'features/CustomFieldsResponses/types';
import {
	PunchupTableContainer,
	PunchupHeaderRow,
	PunchupColumnHeader,
	PunchupActionsHeaderCell,
} from 'features/common/components/Punchup/primitives/table/PunchupTable';
import { useResizableColumns } from 'features/common/hooks/useResizableColumns';
import {
	usePinnedActions,
	ATTENDEE_PINNED_DEFAULTS,
} from 'features/PunchupPages/CustomFieldsResponse/hooks/usePinnedActions';
import { IdParamsType } from 'utils/types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// The sticky actions cell holds the ⋮ menu plus one quick-button slot per PINNED action (see
// usePinnedActions / AttendeeActions). Base = ⋮ + padding; each pinned action adds QUICK_BUTTON_WIDTH.
// Print is pinned by default (ATTENDEE_PINNED_DEFAULTS), so the default cell = 86 + 34 = 120 — i.e. the
// prior Print + ⋮ width, with no visual change for the default case. QUICK_BUTTON_WIDTH must track the
// rendered `.quickBtn` width (icon + padding) in AttendeeActions.module.css.
const ACTIONS_BASE_WIDTH = 86;
const QUICK_BUTTON_WIDTH = 34;

interface PunchupResponsesTableProps extends ResponsesProps {
	visibleStaticKeys: Set<string>;
	/** Discovered custom-field question names (from parent, via discoverQuestions). */
	questions: string[];
	/** Which custom-field question columns the user has explicitly enabled (empty = all hidden). */
	visibleCustomKeys: Set<string>;
	/** Override the default AttendeeActions renderer for each row. Receives the page's order→ticketIds map so the override can drive the bulk "switch all" path. */
	renderRowActions?: (data: ResponsesData, transactionTickets: Record<string, number[]>) => ReactNode;
	/** True while an already-loaded table re-fetches — dims the table with a spinner overlay. */
	isRefetching?: boolean;
}

/**
 * Sort fields supported by the `/events/:event_id/custom-fields/responses`
 * endpoint (verified in `ticketing-api-2.0/cmd/usecases/custom_field.go`):
 * - DB columns: `order_id`, `date`, `amount`, `status`
 * - In-memory sort: `email`, `full_name`
 *
 * `scan_count` is NOT supported by the backend; the Scanned column is therefore
 * not sortable.
 */
const SORT_FIELD_MAP: Record<string, SortField> = {
	'customFields-responses-table-orderId': 'order_id',
	'customFields-responses-table-email': 'email',
	'customFields-responses-table-fullName': 'full_name',
	'customFields-responses-table-amount': 'amount',
	'customFields-responses-table-date-green': 'date',
	'customFields-responses-table-status': 'status',
};

export const PunchupResponsesTable: FC<PunchupResponsesTableProps> = ({
	responsesQueryObject,
	isAttendeesList,
	eventMode,
	sortBy,
	order,
	onSort,
	visibleStaticKeys,
	questions,
	visibleCustomKeys,
	renderRowActions,
	isRefetching,
}) => {
	const { t, i18n } = useTranslation();
	const { id: eventId } = useParams<IdParamsType>();
	const { pinnedIds } = usePinnedActions(eventId, ATTENDEE_PINNED_DEFAULTS);
	const actionsWidth = ACTIONS_BASE_WIDTH + pinnedIds.size * QUICK_BUTTON_WIDTH;
	const { fetchNextPage, hasNextPage, data, isLoading, isFetchingNextPage } = responsesQueryObject;

	const customHeadings = eventMode === 'normal' ? PAID_COLUMN_HEADINGS : REGISTRATION_COLUMN_HEADINGS;

	const allHeadings = useMemo(() => {
		const h = [...customHeadings];
		if (data?.pages?.length) {
			if (isAttendeesList) h.push(...questions);
			else h.splice(RESPONSES_INDEX, 0, ...questions);
		}
		return h;
	}, [customHeadings, data?.pages, questions, isAttendeesList]);

	const showDownloadQR = !!data?.pages?.some((p) => p.some((r) => r.can_download_qr));

	const visibleHeadings = allHeadings.filter((h) => {
		if (customHeadings.includes(h)) return visibleStaticKeys.has(h);
		return visibleCustomKeys.has(h);
	});

	// The QR column is appended manually (not part of visibleHeadings), so it must be given its own grid
	// track or the subgrid runs one track short of the rendered cells (cells spill / the sticky actions
	// track is consumed by the wrong cell). QR sits before the trailing sticky-actions track.
	const columnKeys = isAttendeesList && showDownloadQR ? [...visibleHeadings, 'qr'] : visibleHeadings;
	// actionsWidth (86 base + 34 × pinned) is computed above — see the block comment on ACTIONS_BASE_WIDTH.
	const { gridColumns, setColumnWidth } = useResizableColumns(columnKeys, { hasActionsColumn: true, actionsWidth });

	const getSortDirection = (heading: string) => {
		const sortField = SORT_FIELD_MAP[heading];
		if (!sortField || sortBy !== sortField) return null;
		return order === 'asc' ? 'asc' : ('desc' as const);
	};

	return (
		<>
			<PunchupTableContainer columns={gridColumns} isRefetching={isRefetching}>
				<thead>
					<PunchupHeaderRow>
						{visibleHeadings.map((heading) => {
							const sortField = SORT_FIELD_MAP[heading];
							const label = i18n.exists(heading) ? t(heading) : heading;
							return (
								<PunchupColumnHeader
									key={heading}
									sortable={!!sortField}
									sortDirection={getSortDirection(heading)}
									onSort={sortField ? () => onSort(sortField) : undefined}
									onResize={(w) => setColumnWidth(heading, w)}
								>
									{label}
								</PunchupColumnHeader>
							);
						})}

						{/* QR column must precede the sticky actions header so header & body agree on column
						    order (body renders QR then actions); otherwise the pinned actions header lands over
						    the wrong column on the Attendee List. */}
						{isAttendeesList && showDownloadQR && (
							<PunchupColumnHeader key="qr">{t('customFields-responses-ticketQR')}</PunchupColumnHeader>
						)}

						<PunchupActionsHeaderCell key="actions-header" />
					</PunchupHeaderRow>
				</thead>

				<PunchupTableBody
					responsesQueryObject={responsesQueryObject}
					visibleHeadings={visibleHeadings}
					questions={questions}
					isAttendeesList={isAttendeesList}
					showDownloadQR={showDownloadQR}
					eventMode={eventMode}
					isLoading={isLoading}
					renderRowActions={renderRowActions}
				/>
			</PunchupTableContainer>

			<PunchupLoadMore isDisabled={!hasNextPage} isLoading={isFetchingNextPage} handleClick={() => fetchNextPage()} />
		</>
	);
};
